// API client configuration
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { useAuthStore } from '../store/authStore';
import { supabase } from './supabase-client';

const API_BASE_URL = `https://${projectId}.supabase.co/functions/v1/make-server-bde4f709`;

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

/**
 * Возвращает актуальный access token. getSession() сам обновляет протухший
 * JWT — это устраняет гонку, когда фоновый autoRefreshToken не успел
 * сработать (частый случай в урезанном webview Telegram) и в стор попадал
 * устаревший токен, из-за чего бэкенд отвечал 401. Снапшот стора — фолбэк.
 */
async function getFreshToken(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return session.access_token;
  } catch (err) {
    console.error('getSession failed, falling back to cached token:', err);
  }
  return useAuthStore.getState().accessToken;
}

/**
 * Запрос к нашему бэкенду (`/api/*`) с токеном человека. Ручки публикации там
 * с 05.10.2026 проверяют, кто и от чьего имени публикует, — без заголовка 401.
 */
export async function backendFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = await getFreshToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

/** Для XHR, где `fetch` не годится (прогресс заливки): только сам заголовок. */
export async function backendAuthHeader(): Promise<string | null> {
  const token = await getFreshToken();
  return token ? `Bearer ${token}` : null;
}

/**
 * Базовая функция для выполнения API запросов
 */
async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;
  
  let url = `${API_BASE_URL}${endpoint}`;
  
  // Добавляем query параметры если есть
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  
  const token = await getFreshToken();
  if (!token) {
    supabase.auth.signOut();
    throw new Error('Not authenticated');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };
  
  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    if (isSessionAuthFailure(response.status, errorData)) {
      supabase.auth.signOut();
    }
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

/**
 * True only for a 401 from our OWN auth middleware (index.ts отдаёт
 * { error: 'Unauthorized' } когда JWT протух/невалиден). Ошибки сторонних
 * сервисов (Instagram Graph API), проксируемые бэкендом с 401/403 — напр.
 * мёртвый IG access token, code 190 — НЕ должны убивать сессию приложения и
 * выкидывать пользователя на логин.
 */
function isSessionAuthFailure(status: number, errorData: { error?: string }): boolean {
  return status === 401 && errorData?.error === 'Unauthorized';
}

export const api = {
  get: <T,>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),
  
  post: <T,>(endpoint: string, data?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  postFormData: async <T,>(endpoint: string, formData: FormData, options?: RequestOptions) => {
    const fmToken = await getFreshToken();
    if (!fmToken) {
      supabase.auth.signOut();
      throw new Error('Not authenticated');
    }
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${fmToken}`,
        ...options?.headers,
      },
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      if (isSessionAuthFailure(response.status, errorData)) {
        supabase.auth.signOut();
      }
      throw new Error(errorData.error || `Request failed with status ${response.status}`);
    }
    return response.json() as Promise<T>;
  },
  
  put: <T,>(endpoint: string, data?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: <T,>(endpoint: string, data?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: <T,>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};